// src/components/ReviewsSection.jsx
import React, { useEffect, useMemo, useState } from "react";
import { getReviewsPublic } from "../core/api/reviewsApi";
import ReviewCard from "./ReviewCard";
import ReviewForm from "./ReviewForm";

export default function ReviewsSection() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      setLoading(true);
      setErr("");
      const data = await getReviewsPublic();
      setReviews(Array.isArray(data) ? data : []);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Failed to load reviews");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const sorted = useMemo(() => {
    return [...reviews].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [reviews]);

  return (
    <section
      id="reviews"
      className="py-20 bg-gradient-to-br from-primary/10 via-accent to-primary/20"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-secondary text-sm font-medium border border-secondary shadow-sm">
            Testimonials
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold mt-4 text-secondary">
            What People Say About Us
          </h2>
          <p className="mt-2 text-lg sm:text-xl text-secondary/80">
            Real feedback from our customers using our products.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          {/* LEFT: Reviews carousel/grid */}
          <div className="rounded-2xl border border-secondary/30 bg-white/70 backdrop-blur-sm shadow-lg p-4">
            {loading ? (
              <div className="text-secondary/80 p-4">Loading reviews…</div>
            ) : err ? (
              <div className="rounded-xl border border-red-200 bg-red-50 text-red-700 p-4">
                {err}
              </div>
            ) : sorted.length === 0 ? (
              <div className="text-secondary/80 p-4">
                No reviews yet. Be the first to write one 🙂
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between px-1">
                  <h3 className="text-lg font-semibold text-secondary">Latest Reviews</h3>
                  <span className="text-xs text-secondary/70">
                    Scroll →
                  </span>
                </div>

                {/* Simple scroll-snap carousel (no heavy libs) */}
                <div className="mt-4 flex gap-4 overflow-x-auto pb-3 snap-x snap-mandatory">
                  {sorted.map((r) => (
                    <ReviewCard key={r.id} item={r} />
                  ))}
                </div>

                <p className="mt-2 text-[12px] text-secondary/70 px-1">
                  Tip: You can scroll horizontally to see more.
                </p>
              </>
            )}
          </div>

          {/* RIGHT: Review form */}
          <div className="rounded-2xl border border-secondary/30 bg-white/70 backdrop-blur-sm shadow-lg p-4">
            <ReviewForm onSuccess={load} />
          </div>
        </div>
      </div>
    </section>
  );
}
